import Papa from 'papaparse';
import * as XLSX from 'xlsx';
import { ParsedFileData, FieldMapping } from '../types/geneva-schema';

export interface FieldMetadataInfo {
  arrayContext?: string;
}

export type FieldMetadataMap = Map<string, FieldMetadataInfo>;

interface ParseResult {
  success: boolean;
  data?: ParsedFileData;
  error?: string;
}

export const parseCSVFile = async (file: File): Promise<ParseResult> => {
  return new Promise((resolve) => {
    Papa.parse(file, {
      header: false,
      skipEmptyLines: true,
      complete: (results) => {
        try {
          if (results.errors.length > 0) {
            resolve({
              success: false,
              error: `CSV parsing error: ${results.errors[0].message}`,
            });
            return;
          }

          if (!results.data || results.data.length === 0) {
            resolve({
              success: false,
              error: 'Empty CSV file',
            });
            return;
          }

          const rows = results.data as (string | number | null)[][];
          const headers = (rows[0] as string[]) || [];
          const dataRows = rows.slice(1);

          resolve({
            success: true,
            data: {
              headers: headers.map(h => String(h).trim()),
              rows: dataRows,
              fileName: file.name,
              fileSize: file.size,
              rowCount: dataRows.length,
            },
          });
        } catch (error) {
          resolve({
            success: false,
            error: `Failed to process CSV: ${error instanceof Error ? error.message : 'Unknown error'}`,
          });
        }
      },
      error: (error) => {
        resolve({
          success: false,
          error: `CSV parsing error: ${error.message}`,
        });
      },
    });
  });
};

export const parseExcelFile = async (file: File): Promise<ParseResult> => {
  return new Promise((resolve) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: 'array' });

        // Get the first worksheet
        const worksheetName = workbook.SheetNames[0];
        if (!worksheetName) {
          resolve({
            success: false,
            error: 'No worksheets found in Excel file',
          });
          return;
        }

        const worksheet = workbook.Sheets[worksheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, {
          header: 1,
          defval: null,
        }) as (string | number | null)[][];

        if (jsonData.length === 0) {
          resolve({
            success: false,
            error: 'Empty Excel file',
          });
          return;
        }

        const headers = (jsonData[0] as string[]) || [];
        const dataRows = jsonData.slice(1);

        resolve({
          success: true,
          data: {
            headers: headers.map(h => String(h || '').trim()),
            rows: dataRows,
            fileName: file.name,
            fileSize: file.size,
            rowCount: dataRows.length,
          },
        });
      } catch (error) {
        resolve({
          success: false,
          error: `Failed to parse Excel file: ${error instanceof Error ? error.message : 'Unknown error'}`,
        });
      }
    };

    reader.onerror = () => {
      resolve({
        success: false,
        error: 'Failed to read file',
      });
    };

    reader.readAsArrayBuffer(file);
  });
};

export const parseFile = async (file: File): Promise<ParseResult> => {
  const fileExtension = file.name.split('.').pop()?.toLowerCase();

  switch (fileExtension) {
    case 'csv':
      return parseCSVFile(file);
    case 'xlsx':
    case 'xls':
      return parseExcelFile(file);
    default:
      return {
        success: false,
        error: `Unsupported file type: ${fileExtension}. Please upload a CSV or Excel file.`,
      };
  }
};

export const suggestFieldMappings = (
  headers: string[],
  availableFields: { xmlTag: string; type: string; required: boolean; description?: string }[]
): Record<string, FieldMapping> => {
  const suggestions: Record<string, FieldMapping> = {};

  availableFields.forEach((field) => {
    // Try to find a matching header by comparing xmlTag, description, or partial matches
    let bestMatch = -1;
    let bestScore = 0;

    // For array fields (containing dot), also try matching without the array prefix
    const originalXmlTag = field.xmlTag.includes('.')
      ? field.xmlTag.split('.').pop() || field.xmlTag
      : field.xmlTag;

    headers.forEach((header, index) => {
      const headerLower = header.toLowerCase().trim();
      const fieldLower = field.xmlTag.toLowerCase();
      const originalFieldLower = originalXmlTag.toLowerCase();
      const descriptionLower = (field.description || '').toLowerCase();

      // Exact match with full xmlTag (highest priority)
      if (headerLower === fieldLower) {
        bestMatch = index;
        bestScore = 100;
      }
      // Exact match with original xmlTag (for array fields)
      else if (headerLower === originalFieldLower) {
        if (bestScore < 95) {
          bestMatch = index;
          bestScore = 95;
        }
      }
      // Exact match with description
      else if (headerLower === descriptionLower) {
        if (bestScore < 90) {
          bestMatch = index;
          bestScore = 90;
        }
      }
      // Partial match with full xmlTag
      else if (headerLower.includes(fieldLower) || fieldLower.includes(headerLower)) {
        if (bestScore < 70) {
          bestMatch = index;
          bestScore = 70;
        }
      }
      // Partial match with original xmlTag (for array fields)
      else if (headerLower.includes(originalFieldLower) || originalFieldLower.includes(headerLower)) {
        if (bestScore < 65) {
          bestMatch = index;
          bestScore = 65;
        }
      }
      // Partial match with description
      else if (descriptionLower && (headerLower.includes(descriptionLower) || descriptionLower.includes(headerLower))) {
        if (bestScore < 60) {
          bestMatch = index;
          bestScore = 60;
        }
      }
    });

    if (bestMatch >= 0) {
      suggestions[field.xmlTag] = {
        fieldName: field.xmlTag,
        columnIndex: bestMatch,
        columnName: headers[bestMatch],
        isRequired: field.required,
        dataType: field.type,
      };
    }
  });

  return suggestions;
};

export const validateFieldMappings = (
  mappings: Record<string, FieldMapping>,
  requiredFields: string[]
): { isValid: boolean; errors: string[] } => {
  const errors: string[] = [];

  // Check if all required fields are mapped or have default values
  requiredFields.forEach((fieldName) => {
    if (!mappings[fieldName]) {
      errors.push(`Required field "${fieldName}" is not mapped`);
    }
  });

  // Check for duplicate column mappings (but skip default values which have columnIndex = -1)
  const usedColumns = new Map<number, string>();
  Object.entries(mappings).forEach(([fieldName, mapping]) => {
    if (mapping.columnIndex >= 0) { // Skip default values
      if (usedColumns.has(mapping.columnIndex)) {
        errors.push(
          `Column "${mapping.columnName}" is mapped to multiple fields: "${fieldName}" and "${usedColumns.get(mapping.columnIndex)}"`
        );
      } else {
        usedColumns.set(mapping.columnIndex, fieldName);
      }
    }
  });

  return {
    isValid: errors.length === 0,
    errors,
  };
};

const ARRAY_INDEX_REGEX = /\[(\d+)\]/g;
const hasArrayIndex = (fieldName: string): boolean => /\[\d+\]/.test(fieldName);
const normalizeFieldName = (fieldName: string): string =>
  fieldName.replace(ARRAY_INDEX_REGEX, '');

const getFieldMetadata = (
  fieldName: string,
  metadataMap?: FieldMetadataMap,
): FieldMetadataInfo | undefined => {
  if (!metadataMap) return undefined;
  const directMatch = metadataMap.get(fieldName);
  if (directMatch) {
    return directMatch;
  }

  const normalized = normalizeFieldName(fieldName);
  if (normalized !== fieldName) {
    return metadataMap.get(normalized);
  }

  return undefined;
};

export const transformRowToFormData = (
  row: (string | number | null)[],
  mappings: Record<string, FieldMapping>,
  metadataMap?: FieldMetadataMap
): Record<string, any> => {
  const formData: Record<string, any> = {};
  const arrayData: Record<string, Record<number, Record<string, any>>> = {};

  Object.entries(mappings).forEach(([fieldName, mapping]) => {
    let sourceValue: any;

    // Use default value if columnIndex is -1, otherwise get from row
    if (mapping.columnIndex === -1) {
      sourceValue = mapping.defaultValue;
    } else {
      sourceValue = row[mapping.columnIndex];
    }

    // Skip empty values for column mappings, but allow default values even if they're empty
    if (mapping.columnIndex >= 0 && (sourceValue === null || sourceValue === undefined || sourceValue === '')) {
      return;
    }

    // Type conversion based on field type
    let convertedValue: any = sourceValue;

    switch (mapping.dataType.toLowerCase()) {
      case 'number':
      case 'decimal':
      case 'integer':
        convertedValue = typeof sourceValue === 'string' ? parseFloat(sourceValue) : sourceValue;
        if (isNaN(convertedValue)) {
          convertedValue = null;
        }
        break;
      case 'boolean':
        convertedValue = sourceValue === 'true' || sourceValue === '1' || sourceValue === 1 || sourceValue === true;
        break;
      case 'date':
        // Handle date conversion - this might need adjustment based on your date format requirements
        if (typeof sourceValue === 'string') {
          const date = new Date(sourceValue);
          convertedValue = !isNaN(date.getTime()) ? date.toISOString().split('T')[0] : sourceValue;
        }
        break;
      default:
        convertedValue = sourceValue !== null && sourceValue !== undefined ? String(sourceValue).trim() : '';
    }

    const fieldMetadata = getFieldMetadata(fieldName, metadataMap);
    const isArrayField = Boolean(fieldMetadata?.arrayContext) || hasArrayIndex(fieldName);

    if (!isArrayField) {
      formData[fieldName] = convertedValue;
      return;
    }

    const normalizedFieldName = normalizeFieldName(fieldName);
    const lastDotIndex = normalizedFieldName.lastIndexOf('.');

    if (lastDotIndex === -1 && !fieldMetadata?.arrayContext) {
      // Fallback to treating as regular field if structure can't be determined
      formData[normalizedFieldName] = convertedValue;
      return;
    }

    const arrayKey = fieldMetadata?.arrayContext
      ? fieldMetadata.arrayContext
      : normalizedFieldName.slice(0, lastDotIndex);
    const actualFieldName = normalizedFieldName.slice(lastDotIndex + 1);

    let arrayIndex = 0;
    const indexMatch = fieldName.match(/\[(\d+)\]/);
    if (indexMatch) {
      const parsedIndex = parseInt(indexMatch[1], 10);
      arrayIndex = Number.isNaN(parsedIndex) ? 0 : Math.max(parsedIndex - 1, 0);
    }

    if (!arrayData[arrayKey]) {
      arrayData[arrayKey] = {};
    }

    if (!arrayData[arrayKey][arrayIndex]) {
      arrayData[arrayKey][arrayIndex] = {};
    }

    arrayData[arrayKey][arrayIndex][actualFieldName] = convertedValue;
  });

  // Convert array data to proper array format for form submission
  Object.entries(arrayData).forEach(([arrayName, indexedItems]) => {
    const sortedIndices = Object.keys(indexedItems)
      .map(index => parseInt(index, 10))
      .sort((a, b) => a - b);

    const items = sortedIndices
      .map(index => indexedItems[index])
      .filter(item => item && Object.keys(item).length > 0);

    if (items.length > 0) {
      formData[arrayName] = items;
    }
  });

  return formData;
};
